import React, { useState, useEffect, useMemo } from 'react';
import {
  ShieldAlert, RefreshCcw, AlertTriangle, AlertCircle, Info, Copy, Check,
  Search, Filter, Trash2, Zap, LayoutGrid, Terminal, Play, Server, User,
  Globe, Cpu, ExternalLink, Database, X, Bug
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../supabase';

const GASTIFY_ERROR_STORAGE_KEY = 'gastify_captured_errors';

// Datos de prueba realistas para lucir cuando no hay errores
const MOCK_SENTRY_EVENTS = [
  {
    id: 'f39b1a56-42d8-4f27-8a6c-947b7c25a1e2',
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // Hace 5 min
    name: 'AstroPayGatewayError',
    message: 'Failed to create AstroPay payment transaction: Gateway timeout (504)',
    stack: 'Error: Gateway timeout (504)\n    at Object.createPayment (file:///c:/Users/adpen/Desktop/ProyectoGastify/gastify/astropay-create-payment.ts:18:13)\n    at processTicksAndRejections (node:internal/process/task_queues:95:5)\n    at async handleCheckout (file:///c:/Users/adpen/Desktop/ProyectoGastify/gastify/src/pages/GestionPlan.jsx:144:21)',
    url: 'https://gastify.app/sistema/gestion-plan',
    userEmail: 'adpenia@gmail.com',
    userId: 'usr-9283471',
    organizationId: 'org-premium-active',
    severity: 'fatal',
    source: 'sentry',
    metadata: {
      browser: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/124.0.0.0 Safari/537.36',
      viewport: '1920x1080',
      environment: 'production'
    }
  },
  {
    id: 'da9d2a67-c2a4-4ccb-88bf-623abcc41235',
    timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString(), // Hace 35 min
    name: 'TypeError',
    message: "Cannot read properties of null (reading 'reduce') in arqueo de caja totals",
    stack: "TypeError: Cannot read properties of null (reading 'reduce')\n    at calculateCajaTotals (file:///c:/Users/adpen/Desktop/ProyectoGastify/gastify/src/pages/Caja.jsx:254:32)\n    at renderTotalsCard (file:///c:/Users/adpen/Desktop/ProyectoGastify/gastify/src/pages/Caja.jsx:580:15)\n    at checkRender (react-dom.development.js:2840:11)",
    url: 'https://gastify.app/sistema/caja',
    userEmail: 'cajero_sucursal1@gmail.com',
    userId: 'usr-441294',
    organizationId: 'org-trial-expiring',
    severity: 'error',
    source: 'runtime-interceptor',
    metadata: {
      browser: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_4_1 like Mac OS X) Mobile/15E148 Safari/604.1',
      viewport: '390x844',
      environment: 'production'
    }
  },
  {
    id: 'bb3d2a56-829d-4dd3-b3c9-947ab22b49e1',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // Hace 4 horas
    name: 'UnhandledPromiseRejection',
    message: 'MercadoPago SDK load failure - Network request blocked by AdBlocker',
    stack: 'UnhandledPromiseRejection: MP SDK load failure: connection refused\n    at initMpSDK (file:///c:/Users/adpen/Desktop/ProyectoGastify/gastify/src/pages/VentasPOS.jsx:23:44)\n    at useEffect (file:///c:/Users/adpen/Desktop/ProyectoGastify/gastify/src/pages/VentasPOS.jsx:88:9)',
    url: 'https://gastify.app/sistema/ventas-pos',
    userEmail: 'ventas_pos2@impulzagrupo.com',
    userId: 'usr-339241',
    organizationId: 'org-premium-active',
    severity: 'warning',
    source: 'promise-interceptor',
    metadata: {
      browser: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Firefox/125.0',
      viewport: '1366x768',
      environment: 'production'
    }
  }
];

export default function SentryDashboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [isDbActive, setIsDbActive] = useState(false);
  const [sqlModalOpen, setSqlModalOpen] = useState(false);

  // Cargar eventos (unificar localStorage y Supabase)
  const fetchEvents = async () => {
    setLoading(true);
    try {
      // 1. Obtener de localStorage
      const localRaw = localStorage.getItem(GASTIFY_ERROR_STORAGE_KEY);
      let localEvents = localRaw ? JSON.parse(localRaw) : [];

      // 2. Intentar obtener de Supabase sentry_events
      let dbEvents = [];
      try {
        const { data, error } = await supabase
          .from('sentry_events')
          .select('*')
          .order('timestamp', { ascending: false })
          .limit(100);
        
        if (!error && data) {
          dbEvents = data.map(db => ({
            id: db.event_id,
            timestamp: db.timestamp,
            name: db.error_name,
            message: db.error_message,
            stack: db.stack_trace,
            url: db.url,
            userEmail: db.user_email,
            userId: db.user_id,
            severity: db.severity,
            source: 'supabase',
            metadata: db.metadata || {}
          }));
          setIsDbActive(true);
        } else {
          setIsDbActive(false);
        }
      } catch (err) {
        setIsDbActive(false);
      }

      // Combinar eventos descartando duplicados por ID
      const combined = [...localEvents];
      dbEvents.forEach(dbEv => {
        if (!combined.some(c => c.id === dbEv.id)) {
          combined.push(dbEv);
        }
      });

      // Ordenar por fecha descendente
      combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Si está vacío, cargar los Mocks para que se vea premium e interactivo
      if (combined.length === 0) {
        setEvents(MOCK_SENTRY_EVENTS);
      } else {
        setEvents(combined);
      }
    } catch (e) {
      toast.error('Error al unificar eventos de error');
    } finally {
      setLoading(false);
    }
  };

  // Escuchar eventos en tiempo real disparados por nuestro main.jsx
  useEffect(() => {
    fetchEvents();

    const handleNewErrorCaptured = (e) => {
      const newErr = e.detail;
      setEvents(prev => {
        const alreadyExists = prev.some(item => item.id === newErr.id);
        if (alreadyExists) return prev;
        
        // Agregar al principio
        const updated = [newErr, ...prev];
        // Remover el primer mock si existían mocks
        const cleaned = updated.filter(item => !MOCK_SENTRY_EVENTS.some(m => m.id === item.id && prev.length === MOCK_SENTRY_EVENTS.length));
        return cleaned;
      });
      toast.error(`Nuevo error capturado: ${newErr.name}`, {
        duration: 4000,
        icon: '⚠️'
      });
    };

    window.addEventListener('gastify:new-error-captured', handleNewErrorCaptured);
    return () => {
      window.removeEventListener('gastify:new-error-captured', handleNewErrorCaptured);
    };
  }, []);

  // Borrar registros
  const clearEvents = () => {
    localStorage.removeItem(GASTIFY_ERROR_STORAGE_KEY);
    setEvents(MOCK_SENTRY_EVENTS);
    toast.success('Logs de errores limpiados localmente (restaurando Mocks ilustrativos)');
  };

  // Filtrado de eventos
  const filteredEvents = useMemo(() => {
    return events.filter(ev => {
      const matchesSearch =
        (ev.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ev.message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ev.url || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ev.userEmail || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSeverity = selectedSeverity === 'all' || ev.severity === selectedSeverity;
      const matchesSource = selectedSource === 'all' || ev.source === selectedSource;

      return matchesSearch && matchesSeverity && matchesSource;
    });
  }, [events, searchTerm, selectedSeverity, selectedSource]);

  // KPIs
  const stats = useMemo(() => {
    const total = filteredEvents.length;
    const fatal = filteredEvents.filter(e => e.severity === 'fatal' || e.severity === 'error').length;
    const warning = filteredEvents.filter(e => e.severity === 'warning').length;
    const uniqueUsers = new Set(filteredEvents.map(e => e.userEmail)).size;
    return { total, fatal, warning, uniqueUsers };
  }, [filteredEvents]);

  // Copiar el reporte de error formateado para pegarlo en Antigravity
  const copyForAntigravity = (ev) => {
    const report = `<SENTRY_EVENT_REPORT>
Event ID: ${ev.id}
Timestamp: ${ev.timestamp}
URL del error: ${ev.url}
Usuario Activo: ${ev.userEmail} (ID: ${ev.userId || 'No definido'})
Origen Captura: ${ev.source}
Nivel Severidad: ${ev.severity}

Tipo de Excepción: ${ev.name}
Mensaje detallado: ${ev.message}

-- STACK TRACE EN CONTEXTO --
${ev.stack}

-- METADATOS DE EJECUCIÓN --
Browser Agent: ${ev.metadata?.browser || 'No disponible'}
Resolución Viewport: ${ev.metadata?.viewport || 'No disponible'}
Ambiente: ${ev.metadata?.environment || 'No disponible'}
</SENTRY_EVENT_REPORT>`;

    navigator.clipboard.writeText(report);
    setCopiedId(ev.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('¡Reporte copiado en formato Antigravity! Pegalo en el chat para corregirlo.', {
      duration: 5000,
      icon: '🤖'
    });
  };

  // --- SIMULADORES DE ERRORES REALES ---
  const triggerReferenceError = () => {
    try {
      // Forzar ReferenceError de manera que el catcher de main.jsx lo reciba
      const fn = () => {
        // @ts-ignore
        console.log(variableInexistenteNoDefinida);
      };
      fn();
    } catch (e) {
      // Disparamos la captura manual si no llegó a disparar window.onerror automáticamente
      window.__gastifyCaptureManualError(e, 'error');
    }
  };

  const triggerTypeError = () => {
    try {
      const user = null;
      // @ts-ignore
      user.invocarMetodoInexistente();
    } catch (e) {
      window.__gastifyCaptureManualError(e, 'fatal');
    }
  };

  const triggerPromiseRejection = () => {
    // Generar un rechazo de promesa no manejada
    const rejectedPromise = Promise.reject(new Error("Conexión fallida con el procesador AstroPay: 403 Forbidden"));
    // También gatillar la captura manual para feedback inmediato
    rejectedPromise.catch(err => {
      window.__gastifyCaptureManualError(err, 'warning');
    });
  };

  const triggerCustomReactError = () => {
    const errorObj = new Error("Error en renderizado: Fallo al deserializar catálogo de combos de la sucursal activa.");
    errorObj.name = "ReactRenderException";
    window.__gastifyCaptureManualError(errorObj, 'error');
  };

  // Código SQL para crear la tabla de Supabase
  const sqlCreateCode = `-- SCRIPT PARA CREAR TABLA SENTRY_EVENTS EN SUPABASE

CREATE TABLE IF NOT EXISTS sentry_events (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  event_id TEXT NOT NULL,
  timestamp TIMESTAMPTZ DEFAULT now() NOT NULL,
  error_name TEXT NOT NULL,
  error_message TEXT NOT NULL,
  stack_trace TEXT,
  url TEXT,
  user_email TEXT,
  user_id TEXT,
  severity TEXT DEFAULT 'error',
  metadata JSONB DEFAULT '{}'::jsonb
);

-- Habilitar RLS
ALTER TABLE sentry_events ENABLE ROW LEVEL SECURITY;

-- Políticas de RLS: Permitir insertar a todos los usuarios autenticados
CREATE POLICY "Permitir inserciones globales" ON sentry_events
  FOR INSERT TO authenticated WITH CHECK (true);

-- Permitir consultar únicamente al administrador principal adpenia@gmail.com
CREATE POLICY "Permitir consulta administrador principal" ON sentry_events
  FOR SELECT TO authenticated 
  USING (auth.jwt() ->> 'email' = 'adpenia@gmail.com');
`;

  const copySqlCode = () => {
    navigator.clipboard.writeText(sqlCreateCode);
    toast.success('Código SQL copiado al portapapeles');
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto font-sans animate-nova-in">
      
      {/* HEADER PRINCIPAL */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center shadow-lg shrink-0 text-red-500">
            <ShieldAlert size={24} className="animate-pulse" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-tight">Consola de Telemetría Sentry</h1>
            <p className="text-[11px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mt-0.5">
              Administración exclusiva para adpenia@gmail.com 
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Indicador de Supabase */}
          <div 
            onClick={() => setSqlModalOpen(true)}
            className={`cursor-pointer px-3 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
              isDbActive 
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' 
                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
            }`}
          >
            <Database size={12} />
            {isDbActive ? 'Supabase Sync Activo' : 'Supabase Inactivo (Ver SQL)'}
          </div>

          <button
            onClick={fetchEvents}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white font-bold text-[11px] uppercase tracking-widest hover:bg-slate-800 transition shadow-sm hover:shadow-md"
          >
            <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} /> Refrescar
          </button>
          
          <button
            onClick={clearEvents}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-white text-red-700 font-bold text-[11px] uppercase tracking-widest hover:bg-red-50 transition shadow-sm"
          >
            <Trash2 size={13} /> Limpiar
          </button>
        </div>
      </div>

      {/* METRICAS Y KPIS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow transition-shadow relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Eventos Totales</span>
            <Bug size={14} className="text-zinc-600" />
          </div>
          <div className="text-[28px] font-black text-slate-900 tracking-tight leading-none">{stats.total}</div>
          <p className="text-[10px] text-zinc-500 mt-1 uppercase font-bold tracking-wider">Logs en memoria</p>
          <div className="absolute right-0 bottom-0 w-24 h-1 bg-slate-900"></div>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow transition-shadow relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Errores Críticos</span>
            <AlertCircle size={14} className="text-red-500" />
          </div>
          <div className="text-[28px] font-black text-red-600 tracking-tight leading-none">{stats.fatal}</div>
          <p className="text-[10px] text-zinc-500 mt-1 uppercase font-bold tracking-wider">Requieren atención</p>
          <div className="absolute right-0 bottom-0 w-24 h-1 bg-red-500"></div>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow transition-shadow relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Warnings / Alertas</span>
            <AlertTriangle size={14} className="text-amber-500" />
          </div>
          <div className="text-[28px] font-black text-amber-600 tracking-tight leading-none">{stats.warning}</div>
          <p className="text-[10px] text-zinc-500 mt-1 uppercase font-bold tracking-wider">No fatales</p>
          <div className="absolute right-0 bottom-0 w-24 h-1 bg-amber-500"></div>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow transition-shadow relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500">Usuarios Afectados</span>
            <User size={14} className="text-blue-500" />
          </div>
          <div className="text-[28px] font-black text-blue-700 tracking-tight leading-none">{stats.uniqueUsers}</div>
          <p className="text-[10px] text-zinc-500 mt-1 uppercase font-bold tracking-wider">Emails registrados</p>
          <div className="absolute right-0 bottom-0 w-24 h-1 bg-blue-500"></div>
        </div>
      </div>

      {/* SECCIÓN SIMULADOR DE EXCEPCIONES */}
      <div className="bg-slate-950 text-slate-100 rounded-2xl p-5 mb-6 border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-900 rounded-full blur-3xl opacity-30 -mr-20 -mt-20"></div>
        
        <div className="flex items-center gap-2 mb-3 relative z-10">
          <Zap size={16} className="text-amber-400" />
          <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
            Simulador de Excepciones en Caliente (Sentry Error Testing Console)
          </h2>
        </div>
        
        <p className="text-xs text-slate-400 mb-4 max-w-4xl leading-relaxed">
          Haz clic en cualquiera de los botones de abajo para simular excepciones en tiempo real. Esto lanzará un error capturado de forma inmediata por el interceptor global e impactará el listado. Podrás copiar su reporte de inmediato.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative z-10">
          <button
            onClick={triggerReferenceError}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-red-500 hover:bg-slate-900/60 transition group text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-red-950/50 flex items-center justify-center text-red-500 shrink-0">
              <Play size={12} className="group-hover:scale-125 transition" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-200 leading-tight">ReferenceError</div>
              <div className="text-[8px] text-slate-500">Variable no definida</div>
            </div>
          </button>

          <button
            onClick={triggerTypeError}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-red-500 hover:bg-slate-900/60 transition group text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-red-950/50 flex items-center justify-center text-red-400 shrink-0">
              <Play size={12} className="group-hover:scale-125 transition" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-200 leading-tight">TypeError</div>
              <div className="text-[8px] text-slate-500">null.metodoInvalido()</div>
            </div>
          </button>

          <button
            onClick={triggerPromiseRejection}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-amber-500 hover:bg-slate-900/60 transition group text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-amber-950/50 flex items-center justify-center text-amber-400 shrink-0">
              <Play size={12} className="group-hover:scale-125 transition" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-200 leading-tight">Promise Reject</div>
              <div className="text-[8px] text-slate-500">Fallo de red AstroPay</div>
            </div>
          </button>

          <button
            onClick={triggerCustomReactError}
            className="flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-blue-400 hover:bg-slate-900/60 transition group text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-blue-950/50 flex items-center justify-center text-blue-400 shrink-0">
              <Play size={12} className="group-hover:scale-125 transition" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-200 leading-tight">Custom Exception</div>
              <div className="text-[8px] text-slate-500">React Catalog render crash</div>
            </div>
          </button>
        </div>
      </div>

      {/* GRILLA DE FILTROS Y EVENTOS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        
        {/* LISTADO DE EVENTOS (IZQUIERDA - 2/3 ancho) */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4">
            
            {/* Barra de Filtros */}
            <div className="flex flex-col md:flex-row gap-3 items-center justify-between mb-4">
              <div className="relative w-full md:max-w-xs">
                <input
                  type="text"
                  placeholder="Buscar por error, URL, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[11px] font-bold outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-slate-900 placeholder:text-zinc-400"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={13} />
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                <div className="flex items-center gap-1.5 bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                  <Filter size={11} className="text-zinc-500" />
                  <select
                    value={selectedSeverity}
                    onChange={(e) => setSelectedSeverity(e.target.value)}
                    className="bg-transparent text-[10px] font-black uppercase tracking-wider outline-none text-zinc-700 cursor-pointer"
                  >
                    <option value="all">Todas Severidades</option>
                    <option value="fatal">Fatal / Error</option>
                    <option value="error">Error</option>
                    <option value="warning">Warning</option>
                  </select>
                </div>

                <div className="flex items-center gap-1.5 bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-200">
                  <Cpu size={11} className="text-zinc-500" />
                  <select
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.target.value)}
                    className="bg-transparent text-[10px] font-black uppercase tracking-wider outline-none text-zinc-700 cursor-pointer"
                  >
                    <option value="all">Todos los Orígenes</option>
                    <option value="sentry">Sentry Client SDK</option>
                    <option value="runtime-interceptor">Global Runtime</option>
                    <option value="promise-interceptor">Global Promises</option>
                    <option value="manual">Manual / Simulador</option>
                    <option value="supabase">Base Supabase</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Listado de Tarjetas */}
            <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
              {loading ? (
                <div className="py-20 text-center text-zinc-500 font-bold uppercase tracking-widest text-xs flex flex-col items-center gap-2">
                  <RefreshCcw className="animate-spin text-slate-900" size={24} />
                  <span>Unificando y analizando telemetría...</span>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="py-20 text-center text-zinc-400 font-bold uppercase tracking-widest text-xs">
                  Sin eventos que coincidan con la búsqueda
                </div>
              ) : (
                filteredEvents.map((ev) => {
                  const isSelected = selectedEvent?.id === ev.id;
                  const isMock = MOCK_SENTRY_EVENTS.some(m => m.id === ev.id);
                  
                  // Colores de badges de gravedad
                  const sevColor = {
                    fatal: 'bg-red-950 text-red-400 border-red-900',
                    error: 'bg-red-50 text-red-700 border-red-200',
                    warning: 'bg-amber-50 text-amber-700 border-amber-200',
                    info: 'bg-blue-50 text-blue-700 border-blue-200'
                  }[ev.severity] || 'bg-zinc-50 text-zinc-700 border-zinc-200';

                  return (
                    <div
                      key={ev.id}
                      onClick={() => setSelectedEvent(ev)}
                      className={`cursor-pointer border rounded-xl p-3.5 transition-all text-left relative overflow-hidden ${
                        isSelected
                          ? 'border-slate-900 bg-slate-50/50 shadow-md ring-2 ring-slate-900/5'
                          : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm'
                      }`}
                    >
                      {/* Borde izquierdo decorativo por severidad */}
                      <div className={`absolute top-0 left-0 bottom-0 w-1 ${
                        ev.severity === 'fatal' || ev.severity === 'error' ? 'bg-red-500' : ev.severity === 'warning' ? 'bg-amber-500' : 'bg-zinc-300'
                      }`}></div>

                      <div className="pl-2.5">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[12px] font-black text-slate-900 tracking-tight">{ev.name}</span>
                            
                            {/* Severidad Badge */}
                            <span className={`px-2 py-0.5 rounded border text-[8px] font-black uppercase tracking-widest leading-none ${sevColor}`}>
                              {ev.severity}
                            </span>

                            {/* Origen Badge */}
                            <span className="px-2 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-[8px] font-bold text-zinc-500 uppercase tracking-widest leading-none flex items-center gap-1">
                              <Cpu size={8} /> {ev.source}
                            </span>

                            {isMock && (
                              <span className="px-2 py-0.5 rounded border border-purple-200 bg-purple-50 text-[8px] font-black text-purple-700 uppercase tracking-widest leading-none">
                                MOCK TELEMETRÍA
                              </span>
                            )}
                          </div>
                          
                          <span className="text-[9px] font-bold text-zinc-400">
                            {new Date(ev.timestamp).toLocaleTimeString('es-AR')} ({new Date(ev.timestamp).toLocaleDateString('es-AR')})
                          </span>
                        </div>

                        <p className="text-[11px] font-medium text-zinc-700 leading-snug line-clamp-2 font-mono mb-2 bg-zinc-50 p-1.5 rounded border border-zinc-100">
                          {ev.message}
                        </p>

                        <div className="flex flex-wrap items-center justify-between gap-3 text-[9px] font-bold uppercase tracking-wider text-zinc-500">
                          <span className="flex items-center gap-1 truncate max-w-[280px]">
                            <Globe size={11} className="text-zinc-400 shrink-0" />
                            <span className="truncate">{ev.url}</span>
                          </span>

                          <span className="flex items-center gap-1">
                            <User size={11} className="text-zinc-400" />
                            {ev.userEmail}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* DETALLE EN PROFUNDIDAD (DERECHA - 1/3 ancho) */}
        <div className="lg:col-span-1">
          {selectedEvent ? (
            <div className="bg-slate-900 border border-slate-800 text-slate-100 rounded-xl p-5 shadow-lg space-y-4 sticky top-20 text-left relative overflow-hidden max-h-[800px] flex flex-col">
              <div className="absolute top-0 right-0 w-24 h-24 bg-slate-850 rounded-full blur-xl opacity-20 -mr-6 -mt-6"></div>
              
              {/* Header Detalle */}
              <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-3 shrink-0">
                <div className="min-w-0">
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 leading-none mb-1">Inspección de Incidente</div>
                  <h3 className="text-sm font-black text-slate-100 uppercase tracking-tight truncate leading-tight">
                    {selectedEvent.name}
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 transition"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                {/* Botón Quick Copy para Antigravity */}
                <button
                  onClick={() => copyForAntigravity(selectedEvent)}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-slate-950 font-black text-[11px] uppercase tracking-widest transition-all shadow-md active:scale-95"
                >
                  {copiedId === selectedEvent.id ? (
                    <>
                      <Check size={14} /> ¡Copiado! Listo para pegar
                    </>
                  ) : (
                    <>
                      <Copy size={14} /> Copiar Detalle para Antigravity
                    </>
                  )}
                </button>

                {/* Mensaje de Error Completo */}
                <div className="space-y-1">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Mensaje de Error</span>
                  <div className="bg-slate-950 p-3 rounded-lg border border-slate-800 text-[11px] font-mono text-slate-200 leading-relaxed max-h-32 overflow-y-auto break-all selection:bg-teal-900">
                    {selectedEvent.message}
                  </div>
                </div>

                {/* Stack Trace */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Terminal size={11} className="text-slate-500" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Pila de ejecución (Stack Trace)</span>
                  </div>
                  <pre className="bg-slate-955 p-3 rounded-lg border border-slate-800 text-[9px] font-mono text-red-300 leading-relaxed overflow-x-auto overflow-y-auto max-h-56 select-all scrollbar-thin">
                    {selectedEvent.stack}
                  </pre>
                </div>

                {/* Contexto de usuario */}
                <div className="border-t border-slate-800 pt-3 space-y-2">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Contexto de Seguridad</span>
                  <div className="grid grid-cols-2 gap-2 text-[10px] leading-tight">
                    <div className="bg-slate-950/40 p-2 rounded border border-slate-800/40">
                      <span className="block text-[8px] font-bold text-slate-500 uppercase tracking-wide">Usuario Email</span>
                      <span className="font-semibold text-slate-300 truncate block">{selectedEvent.userEmail}</span>
                    </div>
                    <div className="bg-slate-950/40 p-2 rounded border border-slate-800/40">
                      <span className="block text-[8px] font-bold text-slate-500 uppercase tracking-wide">Id Usuario</span>
                      <span className="font-semibold text-slate-300 block truncate">{selectedEvent.userId || 'Anon / N/A'}</span>
                    </div>
                    <div className="bg-slate-950/40 p-2 rounded border border-slate-800/40 col-span-2">
                      <span className="block text-[8px] font-bold text-slate-500 uppercase tracking-wide">Url Afectada</span>
                      <span className="font-semibold text-teal-400 block truncate font-mono text-[9px]">{selectedEvent.url}</span>
                    </div>
                  </div>
                </div>

                {/* Datos técnicos */}
                <div className="border-t border-slate-800 pt-3 space-y-2">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">Fierros & Ambiente</span>
                  <div className="grid grid-cols-3 gap-2 text-[9px] leading-tight">
                    <div className="bg-slate-950/40 p-1.5 rounded border border-slate-800/40 text-center">
                      <span className="block text-[7px] font-bold text-slate-500 uppercase">Ambiente</span>
                      <span className="font-bold text-slate-300">{selectedEvent.metadata?.environment || 'prod'}</span>
                    </div>
                    <div className="bg-slate-950/40 p-1.5 rounded border border-slate-800/40 text-center">
                      <span className="block text-[7px] font-bold text-slate-500 uppercase">Viewport</span>
                      <span className="font-bold text-slate-300">{selectedEvent.metadata?.viewport || 'unknown'}</span>
                    </div>
                    <div className="bg-slate-950/40 p-1.5 rounded border border-slate-800/40 text-center">
                      <span className="block text-[7px] font-bold text-slate-500 uppercase">Captura</span>
                      <span className="font-bold text-slate-300 truncate block">{selectedEvent.source}</span>
                    </div>
                  </div>
                </div>

                {/* User Agent */}
                <div className="bg-slate-950 p-2 rounded text-[8px] font-mono text-slate-400 border border-slate-800 overflow-x-auto truncate">
                  UserAgent: {selectedEvent.metadata?.browser || 'No disponible'}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-400 font-bold uppercase tracking-widest text-[10px] space-y-3 sticky top-20">
              <Bug size={32} className="mx-auto text-zinc-300" strokeWidth={1.5} />
              <p>Selecciona un incidente del listado para inspeccionar su pila de llamadas, metadatos técnicos e iniciar la corrección asistida.</p>
            </div>
          )}
        </div>

      </div>

      {/* MODAL CONFIGURACIÓN SQL SUPABASE */}
      {sqlModalOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 text-slate-100 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[85vh]">
            
            <div className="px-5 py-4 border-b border-slate-800 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2">
                <Database className="text-teal-400 animate-pulse" size={18} />
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-100">Instalación Tabla Supabase</h3>
              </div>
              <button 
                onClick={() => setSqlModalOpen(false)}
                className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 transition"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4 text-left">
              <p className="text-xs text-slate-400 leading-relaxed">
                Por defecto, los errores se capturan de forma extremadamente robusta en el almacenamiento local de tu navegador. Sin embargo, para activar la <strong>Sincronización en la nube (Cloud Sync)</strong> y poder ver desde esta consola los errores que les ocurren a otros usuarios o cajeros en producción, debes crear la siguiente tabla en Supabase.
              </p>

              <div className="space-y-2">
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-wider text-slate-400">
                  <span>Código SQL (Ejecutar en Supabase SQL Editor)</span>
                  <button 
                    onClick={copySqlCode}
                    className="flex items-center gap-1 px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 transition border border-slate-700 text-teal-400 shadow-sm"
                  >
                    <Copy size={11} /> Copiar SQL
                  </button>
                </div>

                <pre className="bg-slate-950 p-4 rounded-xl text-[10px] font-mono text-emerald-400 overflow-auto max-h-72 border border-slate-800 selection:bg-slate-800">
                  {sqlCreateCode}
                </pre>
              </div>

              <div className="rounded-xl bg-teal-950/20 border border-teal-900/50 p-3.5 flex items-start gap-3">
                <Info size={16} className="text-teal-400 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-400 leading-normal">
                  <strong>Seguridad RLS robusta garantizada:</strong> Las políticas configuradas permiten a cualquier sucursal o cajero insertar registros de error, pero restringen la consulta de la tabla <em>únicamente</em> al administrador principal <strong>adpenia@gmail.com</strong>.
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-800 flex justify-end shrink-0">
              <button 
                onClick={() => setSqlModalOpen(false)}
                className="px-5 py-2.5 rounded-lg bg-teal-500 hover:bg-teal-600 text-slate-950 font-black text-[11px] uppercase tracking-widest transition shadow-md"
              >
                Entendido
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ESTILOS Y ANIMACIONES */}
      <style>{`
        .animate-nova-in { 
          animation: novaFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        @keyframes novaFadeIn { 
          from { opacity: 0; transform: translateY(12px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        /* Custom scrollbar para la pila de errores */
        ::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        ::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.3);
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.3);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.6);
        }
      `}</style>

    </div>
  );
}
